<template>
  <section class="section-grid">
    <PhonesGridHeadline />
    <mq-layout class="grid-summary-m" :mq="['sm', 'md']">
      <div class="grid-summary-inner"><span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ phones ? phones.Total : 0 }}</span></div>
      <b-button class="is-transparent grid-search-m">
        <icon-base width="17" height="17" view-box="0 0 15 15" icon-name="search"><icon-search /></icon-base>
      </b-button>
    </mq-layout>
    <PhonesGridContent @toggle-phone-edit="onTogglePhoneEdit" />
    <mq-layout class="grid-summary" mq="lg">
      <div class="grid-summary-inner">
        <span class="summary-label">{{ $t("Grid.TotalRecordsCount") }}:</span> <span class="summary-val">{{ phones ? phones.Total : 0 }}</span>
      </div>
    </mq-layout>
  </section>
</template>

<script lang="ts">
import VueMq from "vue-mq";
import { Component, Vue, Watch } from "vue-property-decorator";
import { Action, Getter } from "vuex-class";
import { GridColumn } from "@/models/GridColumn";
import { PhoneWithChannel } from "@/models/phones/PhoneWithChannel";
import { TableSettings, TableSettingsStateColumn } from "@/models/TableSettings";
import PhonesGridHeadline from "./PhonesGridHeadline.vue";
import PhonesGridContent from "./PhonesGridContent.vue";
import IconBase from "../IconBase.vue";
import IconSearch from "../icons/IconSearch.vue";
import IconCaretDown from "../icons/IconCaretDown.vue";
import IconCaretRight from "../icons/IconCaretRight.vue";
import IconCaretLeft from "../icons/IconCaretLeft.vue";
import i18n from "../../i18n";

Vue.use(VueMq, {
  breakpoints: {
    sm: 576,
    md: 992,
    lg: Infinity,
  },
  defaultBreakpoint: "sm",
});

const namespace = "phonesModule";

@Component({
  components: {
    PhonesGridHeadline,
    PhonesGridContent,
    IconBase,
    IconSearch,
    IconCaretDown,
    IconCaretRight,
    IconCaretLeft,
  },
})
export default class PhonesGrid extends Vue {

  //#region Vuex getters and actions

  @Getter("columns", { namespace }) columns!: GridColumn[];
  @Getter("phones", { namespace }) phones!: PhoneWithChannel[];
  @Getter("selectedPhonesGridColumnsIds", { namespace }) selectedPhonesGridColumnsIds!: number[];
  @Getter("tableSettings", { namespace }) tableSettings!: TableSettings;
  @Action("setColumns", { namespace }) setColumns!: (columns: GridColumn[]) => void;

  //#endregion Vuex getters and actions


  //#region Properties

  gridColumns: GridColumn[] = [
    { id: 0, name: "ID", label: "ID", searchable: true, sortable: true, visible: false, forbidden: false },
    { id: 1, name: "PhoneNumber", label: i18n.t("Phones.Grid.Columns.PhoneNumber").toString(), class: "cell-phone", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 2, name: "RedirectPhone", label: i18n.t("Phones.Grid.Columns.RedirectPhone").toString(), class: "cell-receive-call", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 3, name: "ClientTitle", label: i18n.t("Phones.Grid.Columns.Client").toString(), class: "cell-client", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 4, name: "CreateDate", label: i18n.t("Phones.Grid.Columns.CreateDate").toString(), class: "cell-date", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 5, name: "CallsInLast30Days", label: i18n.t("Phones.Grid.Columns.CallsInLast30Days").toString(), class: "cell-calls-number", searchable: true, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 6, name: "ChannelMetadataType", label: i18n.t("Phones.Grid.Columns.ChannelMetadataType").toString(), class: "cell-advertising-channel", searchable: false, sortable: true, visible: true, forbidden: false, width: 128 },
    { id: 7, name: "ChannelName", label: i18n.t("Phones.Grid.Columns.ChannelName").toString(), class: "cell-channel-name", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 8, name: "NumberOfAnsweredCalls", label: i18n.t("Phones.Grid.Columns.AnsweredCalls").toString(), class: "cell-answered-calls", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 9, name: "NumberOfMissedCalls", label: i18n.t("Phones.Grid.Columns.MissedCalls").toString(), class: "cell-missed-calls", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 10, name: "PercentageResponse", label: i18n.t("Phones.Grid.Columns.PercentageResponse").toString(), class: "cell-percentage-response", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 11, name: "AverageCallSeconds", label: i18n.t("Phones.Grid.Columns.AverageCall").toString(), class: "cell-average-call", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 12, name: "TotalCallSeconds", label: i18n.t("Phones.Grid.Columns.TotalCallsTime").toString(), class: "cell-total-calls", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    { id: 13, name: "PhoneStatus", label: i18n.t("Phones.Grid.Columns.PhoneStatus").toString(), class: "cell-status", searchable: false, sortable: false, visible: true, forbidden: false, width: 128 },
    {
      id: 1000,
      name: "Actions",
      label: i18n.t("Phones.Grid.Columns.Actions").toString(),
      searchable: false,
      sortable: false,
      visible: true,
      forbidden: false,
    },
  ];

  //#endregion Properties


  //#region Watchers

  @Watch("tableSettings", { immediate: true })
  onTableSettingsChange(newValue: TableSettings, oldValue: TableSettings): void {
    if (newValue === undefined) {
      return;
    } else {
      this.updateColumns(newValue);
    }
  }

  //#endregion Watchers


  //#region Getters

  get gridColumnsFiltered(): GridColumn[] {
    // if no selected columns (like on first page load), select all columns
    if (this.selectedPhonesGridColumnsIds.length === 0) {
      for (let i = 0; i < this.gridColumns.length; i++) {
        if (this.gridColumns[i].visible && !this.gridColumns[i].forbidden) {
          this.selectedPhonesGridColumnsIds.push(this.gridColumns[i].id);
        }
      }
    }
    for (let i = 0; i < this.gridColumns.length; i++) {
      this.gridColumns[i].visible = this.selectedPhonesGridColumnsIds.indexOf(this.gridColumns[i].id) >= 0;
    }
    return this.gridColumns;
  }

  //#region Getters


  //#region Lifecycle

  mounted(): void {
    this.setColumns(this.gridColumnsFiltered);
  }

  //#endregion Lifecycle


  //#region Handlers

  onTogglePhoneEdit() {
    this.$emit('toggle-phone-edit');
  }

  //#endregion Handlers


  //#region Private methods

  private updateColumns(tableSettings: TableSettings) {
    const newColumns: GridColumn[] = JSON.parse(JSON.stringify(this.columns));
    for (let i = 0; i < newColumns.length; i++) {
      const tableSettingsColumn: TableSettingsStateColumn | undefined = tableSettings?.StateObject.columns.find((column: TableSettingsStateColumn) => {
        let columnName: string | undefined = column.field;
        if (!columnName) {
          columnName = column.title;
        }
        return columnName === newColumns[i].name;
      });
      newColumns[i].visible = !tableSettingsColumn || tableSettingsColumn && !tableSettingsColumn.hidden;
      if (tableSettingsColumn && tableSettingsColumn.hidden) {
        newColumns[i].visible = false;
      }
    }
    this.setColumns(newColumns);
  }

  //#endregion Private methods
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables";

.section-grid {
  background: #fff;
  border: 1px solid #d3d6e2;
  border-radius: 4px;
  margin-bottom: 30px;
}
.grid-summary {
  padding: 8px 23px 9px;
  border-top: 1px solid #d3d6e2;
  .grid-summary-inner {
    .summary-label {
      font-weight: 600;
    }
  }
}
.grid-summary-m {
  height: 49px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 2px solid #435c65;
  .button.grid-search-m {
    padding: 10px 15px;
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 49px;
  }
  .grid-summary-inner {
    padding: 10px 15px;
    .summary-label {
      font-weight: 600;
    }
  }
}

html[dir="ltr"] {
}
</style>
